import { useCallback, useEffect, useState } from "react";
import { Plus, Repeat, Pencil, Trash2, Loader2, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatTL, formatApiErrorDetail } from "../lib/api";
import { getCatMeta } from "../lib/categoryMeta";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../context/AuthContext";

const emptyForm = () => ({ type: "expense", amount: "", category: "", description: "", day_of_month: "1" });

export default function RecurringPage() {
  const { user } = useAuth();
  const perms = user?.permissions || {};
  const [rules, setRules] = useState([]);
  const [categories, setCategories] = useState({ income: [], expense: [] });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/recurring");
    setRules(data);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);

  const openModal = (rule) => {
    setEditing(rule || null);
    setForm(rule
      ? { type: rule.type, amount: String(rule.amount), category: rule.category, description: rule.description, day_of_month: String(rule.day_of_month) }
      : emptyForm());
    setModalOpen(true);
  };

  const catList = form.type === "income" ? categories.income : categories.expense;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.category) { toast.error("Lütfen bir kategori seçin"); return; }
    setSaving(true);
    try {
      const payload = {
        type: form.type,
        amount: parseFloat(form.amount),
        category: form.category,
        description: form.description,
        day_of_month: parseInt(form.day_of_month, 10),
        active: editing ? editing.active : true,
      };
      if (editing) {
        await api.put(`/recurring/${editing.id}`, payload);
        toast.success("Kural güncellendi");
      } else {
        await api.post("/recurring", payload);
        toast.success("Tekrarlayan işlem eklendi. Her ay otomatik işlenecek.");
      }
      setModalOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (rule) => {
    try {
      await api.put(`/recurring/${rule.id}`, { ...rule, active: !rule.active });
      load();
    } catch {
      toast.error("Güncellenemedi");
    }
  };

  const handleDelete = async (rule) => {
    try {
      await api.delete(`/recurring/${rule.id}`);
      toast.success("Kural silindi");
      load();
    } catch {
      toast.error("Silinemedi");
    }
  };

  return (
    <div className="space-y-6 fade-in-up" data-testid="recurring-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">Sabit İşlemler</h1>
          <p className="text-muted-foreground text-sm mt-1">Kira, fatura, maaş gibi tekrarlayan gelir-giderler her ay otomatik eklenir</p>
        </div>
        {perms.add && (
          <Button data-testid="add-recurring-button" onClick={() => openModal(null)} className="rounded-full font-semibold">
            <Plus className="w-4 h-4 mr-1" /> Yeni Sabit İşlem
          </Button>
        )}
      </div>

      <div className="bg-card border border-border rounded-2xl p-4" data-testid="recurring-list">
        {rules.length === 0 ? (
          <div className="py-12 text-center space-y-2">
            <Repeat className="w-8 h-8 text-muted-foreground mx-auto" />
            <p data-testid="no-recurring-message" className="text-sm text-muted-foreground">
              Henüz sabit işlem yok. Kira veya maaş gibi tekrarlayan işlemleri ekleyin.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border/50">
            {rules.map((rule) => {
              const { Icon, color } = getCatMeta(categories.meta, rule.category, rule.type);
              return (
              <div key={rule.id} data-testid={`recurring-item-${rule.id}`}
                className={`flex items-center gap-3 p-3 rounded-xl hover:bg-secondary/50 transition-colors group ${!rule.active ? "opacity-50" : ""}`}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}26` }}>
                  <Icon className="w-4 h-4" style={{ color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{rule.category}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    Her ayın {rule.day_of_month}. günü{rule.description ? ` · ${rule.description}` : ""}
                  </p>
                </div>
                <p className={`text-sm font-semibold shrink-0 ${rule.type === "income" ? "text-emerald-400" : "text-red-400"}`}>
                  {rule.type === "income" ? "+" : "-"}{formatTL(rule.amount)}
                </p>
                <Switch data-testid={`recurring-toggle-${rule.id}`} checked={rule.active} disabled={!perms.edit}
                  onCheckedChange={() => toggleActive(rule)} className="shrink-0" />
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  {perms.edit && (
                  <button data-testid={`edit-recurring-${rule.id}`} onClick={() => openModal(rule)}
                    className="p-2 rounded-lg hover:bg-secondary text-muted-foreground hover:text-primary transition-colors">
                    <Pencil className="w-4 h-4" />
                  </button>
                  )}
                  {perms.delete && (
                  <button data-testid={`delete-recurring-${rule.id}`} onClick={() => handleDelete(rule)}
                    className="p-2 rounded-lg hover:bg-secondary text-muted-foreground hover:text-red-400 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent data-testid="recurring-modal" className="bg-card border-border sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Sabit İşlemi Düzenle" : "Yeni Sabit İşlem"}</DialogTitle>
            <DialogDescription className="sr-only">Her ay otomatik eklenecek gelir veya gider tanımlayın</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <button type="button" data-testid="recurring-type-income" onClick={() => setForm({ ...form, type: "income", category: "" })}
                className={`flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold border transition-colors ${
                  form.type === "income" ? "bg-emerald-500/15 border-emerald-500 text-emerald-400" : "border-border text-muted-foreground hover:bg-secondary"
                }`}>
                <TrendingUp className="w-4 h-4" /> Gelir
              </button>
              <button type="button" data-testid="recurring-type-expense" onClick={() => setForm({ ...form, type: "expense", category: "" })}
                className={`flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold border transition-colors ${
                  form.type === "expense" ? "bg-red-500/15 border-red-500 text-red-400" : "border-border text-muted-foreground hover:bg-secondary"
                }`}>
                <TrendingDown className="w-4 h-4" /> Gider
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="rec-amount">Tutar (₺)</Label>
                <Input id="rec-amount" data-testid="recurring-amount-input" type="number" step="0.01" min="0.01" required
                  placeholder="0,00" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rec-day">Ayın günü (1-31)</Label>
                <Input id="rec-day" data-testid="recurring-day-input" type="number" min="1" max="31" required
                  value={form.day_of_month} onChange={(e) => setForm({ ...form, day_of_month: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Kategori</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="recurring-category-select">
                  <SelectValue placeholder="Kategori seçin" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {catList.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rec-desc">Açıklama (isteğe bağlı)</Label>
              <Textarea id="rec-desc" data-testid="recurring-description-input" rows={2}
                placeholder="Örn: Ev kirası" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <Button data-testid="recurring-save-button" type="submit" disabled={saving} className="w-full rounded-full font-semibold">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editing ? "Güncelle" : "Kaydet"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
